import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { AccountService, CredentialsService, InputError, MatchOtherValidator } from '@ebeihilfe/core';
import { OverlayService } from '@ebeihilfe/web-logic';
import { NoteComponent } from '@ebeihilfe/web-shared/note';
import { PasswordInputComponent, PasswordInputOptions } from '@ebeihilfe/web-shared/password-input';
import { TextInputComponent } from '@ebeihilfe/web-shared/text-input';
import { UsernameInputComponent } from '@ebeihilfe/web-shared/username-input/username-input.component';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'portal-validation-form',
  templateUrl: './validation-form.component.html',
  styleUrls: ['./validation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    RouterLink,
    NoteComponent,
    MatCardModule,
    UsernameInputComponent,
    TextInputComponent,
    PasswordInputComponent,
  ],
})
export class ValidationFormComponent implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly spinnerService = inject(OverlayService);

  public activationCodeErrors: InputError[] = [
    {
      errorCode: 'required',
      errorMessage: _('validation.activationCode.required'),
    },
  ];
  public passwordOptions: Partial<PasswordInputOptions> = {
    hint: _('info_text.input.set_password'),
    createPasswordMode: true,
    autocomplete: 'new-password',
    id: 'newPassword',
    label: 'label.password',
    placeholder: 'label.password',
  };
  public passwordRepeatOptions: Partial<PasswordInputOptions> = {
    hint: _('info_text.input.passwordRepeat'),
    createPasswordMode: false,
    autocomplete: 'new-password',
    id: 'passwordRepeat',
    label: 'label.passwordRepeat',
    placeholder: 'label.passwordRepeat',
    errors: [
      {
        errorCode: 'matchOther',
        errorMessage: _('validation.password.not_matching'),
      },
    ],
  };
  public response = signal<{ success: boolean; show: boolean; message: string } | null>(null);
  public showSuccess = signal(false);
  public showForm = computed(() => this.showSuccess() === false);
  public showResponseMessage = computed(() => !this.showSuccess() && !!this.response()?.show);
  public successEventEmitter = output<boolean>();
  public validationForm: FormGroup;

  public get activationCode(): AbstractControl<string> {
    return this.validationForm.get('code');
  }

  public get password(): AbstractControl<string> {
    return this.validationForm.get('password');
  }

  public get passwordRepeated(): AbstractControl<string> {
    return this.validationForm.get('passwordRepeat');
  }

  public get username(): AbstractControl<string> {
    return this.validationForm.get('personalNumber');
  }

  public ngOnInit(): void {
    this.validationForm = this.createForm();

    const params = this.route.snapshot.params;
    if (params.code) {
      this.username.setValue(params.pn);
      this.activationCode.setValue(params.code);
    }
  }

  public async submitValidationForm(): Promise<void> {
    if (this.validationForm?.invalid ?? true) {
      return;
    }

    this.spinnerService.show();

    this.validateEmailAndSetPassword({
      personalNumber: this.username.value.trim(),
      password: this.password.value.trim(),
      passwordRepeated: this.passwordRepeated.value.trim(),
      code: this.activationCode.value.trim(),
    }).subscribe({
      next: async (success) => {
        if (!success) {
          return;
        }

        this.setSuccess();

        this.spinnerService.hide();
      },
      error: this.handleValidationError.bind(this),
    });
  }

  private createForm(): FormGroup {
    return new FormGroup({
      personalNumber: new FormControl(''),
      code: new FormControl('', Validators.required),
      password: new FormControl(''),
      passwordRepeat: new FormControl('', [MatchOtherValidator('password')]),
    });
  }

  private handleValidationError(error: { message: string }): void {
    this.response.set({
      message: error.message,
      success: false,
      show: true,
    });

    this.spinnerService.hide();
    return;
  }

  private setSuccess(): void {
    this.showSuccess.set(true);
    this.credentialsService.resetPin();
    this.successEventEmitter.emit(true);
  }

  private validateEmailAndSetPassword({
    personalNumber,
    code,
    password,
    passwordRepeated,
  }: {
    personalNumber: string;
    code: string;
    password: string;
    passwordRepeated: string;
  }): Observable<boolean> {
    return this.accountService
      .validateEmailAndSetPassword({
        personalNumber,
        code,
        password,
        passwordRepeated,
      })
      .pipe(
        map((response) => {
          if (response.success === false) {
            throw response;
          }
          return true;
        }),
        takeUntilDestroyed(this.destroyRef),
      );
  }
}
